import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Receipt, ImageIcon, Trash2, Search, Wallet } from 'lucide-react';
import api, { fmtIDR, fmtDate, currentMonth, API } from '@/lib/api';
import { EXPENSE_CATEGORIES, catMeta } from '@/lib/expenseCategories';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';

export default function ExpensesReport() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';
  const [month, setMonth] = useState(currentMonth());
  const [catFilter, setCatFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [receiptFor, setReceiptFor] = useState(null); // movement

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ kind: 'out', month });
      if (catFilter !== 'all') params.set('category', catFilter);
      const [m, s] = await Promise.all([
        api.get(`/cash/movements?${params}`),
        api.get(`/cash/expenses/summary?month=${month}`),
      ]);
      setExpenses(m.data);
      setSummary(s.data);
    } catch (e) {
      console.error('[ExpensesReport] gagal load:', e);
      toast.error(e?.response?.data?.detail || 'Gagal memuat');
    }
  }, [month, catFilter]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!query) return expenses;
    const q = query.toLowerCase();
    return expenses.filter((e) =>
      (e.recipient_name || '').toLowerCase().includes(q)
      || (e.description || '').toLowerCase().includes(q)
      || (e.created_by_name || '').toLowerCase().includes(q)
    );
  }, [expenses, query]);

  const categoryMap = useMemo(() => {
    const m = {};
    EXPENSE_CATEGORIES.forEach((c) => { m[c.value] = { ...c, total: 0, count: 0 }; });
    (summary?.by_category || []).forEach((sc) => {
      if (m[sc.category]) { m[sc.category].total = sc.total; m[sc.category].count = sc.count; }
    });
    return m;
  }, [summary]);

  const doDelete = async (m) => {
    try {
      await api.delete(`/cash/movements/${m.id}`);
      toast.success(`Pengeluaran dihapus. Saldo dikembalikan.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal hapus'); }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="w-full sm:w-40">
          <Label>Bulan</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="expense-month-filter" />
        </div>
        <div className="w-full sm:w-56">
          <Label>Kategori</Label>
          <Select value={catFilter} onValueChange={setCatFilter}>
            <SelectTrigger data-testid="expense-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kategori</SelectItem>
              {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label>Cari</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Nama penerima, deskripsi, petugas..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="expense-search" />
          </div>
        </div>
      </div>

      {/* Category summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {EXPENSE_CATEGORIES.map((c) => {
          const stat = categoryMap[c.value] || { total: 0, count: 0 };
          return (
            <Card key={c.value} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${c.color}`}>
                    <c.icon className="h-4 w-4" />
                  </div>
                  <div className="text-xs text-muted-foreground">{c.label}</div>
                </div>
                <div className="text-xl font-heading font-semibold tabular-nums" data-testid={`cat-total-${c.value}`}>{fmtIDR(stat.total)}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{stat.count} entri bulan ini</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Grand total */}
      <div className="rounded-xl border bg-gradient-to-r from-rose-50 to-background p-4 flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
          <Wallet className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Total Pengeluaran Bulan Ini</div>
          <div className="text-2xl font-heading font-semibold tabular-nums text-rose-700" data-testid="expense-grand-total">{fmtIDR(summary?.total || 0)}</div>
        </div>
      </div>

      {/* Detail table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Receipt className="h-4 w-4" />Detail Pengeluaran ({filtered.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">Tidak ada pengeluaran cocok.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="expenses-detail-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Tanggal</TableHead>
                    <TableHead>Kategori</TableHead>
                    <TableHead>Untuk</TableHead>
                    <TableHead>Deskripsi</TableHead>
                    <TableHead>Petugas</TableHead>
                    <TableHead className="text-center">Bukti</TableHead>
                    <TableHead className="text-right">Nominal</TableHead>
                    {isOwner && <TableHead className="text-right">Aksi</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((e) => {
                    const cm = catMeta(e.category);
                    const Icon = cm.icon;
                    return (
                      <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                        <TableCell className="text-xs whitespace-nowrap text-muted-foreground">{fmtDate(e.created_at)}</TableCell>
                        <TableCell>
                          <Badge className={`${cm.color} border-0`}>
                            {Icon && <Icon className="h-3 w-3 mr-1" />}
                            {cm.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{e.recipient_name || '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[240px] truncate" title={e.description || ''}>{e.description || '-'}</TableCell>
                        <TableCell className="text-xs">
                          {e.created_by_name || '-'}
                          <div className="text-[10px] text-muted-foreground capitalize">{e.created_by_role}</div>
                        </TableCell>
                        <TableCell className="text-center">
                          {e.has_receipt ? (
                            <Button size="sm" variant="outline" onClick={() => setReceiptFor(e)} data-testid={`view-receipt-${e.id}`}>
                              <ImageIcon className="h-3.5 w-3.5 mr-1" />Lihat
                            </Button>
                          ) : (
                            <Badge variant="destructive" className="text-[10px]">Tidak ada</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-semibold text-rose-700">-{fmtIDR(e.amount)}</TableCell>
                        {isOwner && (
                          <TableCell className="text-right">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" data-testid={`delete-expense-${e.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Hapus pengeluaran ini?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {fmtIDR(e.amount)} untuk {e.recipient_name} akan dihapus. Saldo kas akan dikembalikan otomatis.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Batal</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => doDelete(e)} className="bg-rose-600 hover:bg-rose-700">Ya, Hapus</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ReceiptDialog movement={receiptFor} onClose={() => setReceiptFor(null)} />
    </div>
  );
}

function ReceiptDialog({ movement, onClose }) {
  const [src, setSrc] = React.useState(null);
  useEffect(() => {
    if (!movement) { setSrc(null); return; }
    let obj = null;
    api.get(`/cash/movements/${movement.id}/receipt`, { responseType: 'blob' })
      .then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); })
      .catch(() => toast.error('Gagal muat bukti'));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [movement]);
  return (
    <Dialog open={!!movement} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="receipt-dialog">
        <DialogHeader><DialogTitle>Bukti Pengeluaran</DialogTitle></DialogHeader>
        {movement && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-muted-foreground">Kategori:</span> <b>{catMeta(movement.category).label}</b></div>
              <div><span className="text-muted-foreground">Nominal:</span> <b className="text-rose-700">{fmtIDR(movement.amount)}</b></div>
              <div><span className="text-muted-foreground">Untuk:</span> <b>{movement.recipient_name || '-'}</b></div>
              <div><span className="text-muted-foreground">Petugas:</span> <b>{movement.created_by_name} <span className="text-xs text-muted-foreground capitalize">({movement.created_by_role})</span></b></div>
              <div className="col-span-2"><span className="text-muted-foreground">Waktu:</span> <b>{fmtDate(movement.created_at)}</b></div>
              {movement.description && <div className="col-span-2 italic text-muted-foreground">"{movement.description}"</div>}
            </div>
            {src ? (
              <img src={src} alt="receipt" className="w-full max-h-[65vh] object-contain rounded-lg border" data-testid="receipt-img" />
            ) : (
              <div className="text-center py-12 text-sm text-muted-foreground">Memuat bukti...</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
